// <copyright file="offer-advance-to-awaiting-order.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../webmodule-common/other/ui/modal-confirmation';
import { FabricationJobState } from '../../api/fabrication-job-api';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';
import { getApiFactory } from '../../api/api-injector';
import { getFabricationJobStateLabel } from '../data/fabrication-job-helper-functions';
import { LangFabricationJobActions } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-actions-lang';

/**
 * After a job goes onto a purchase order, offers to move it from Planned to Awaiting Order -
 * the state that says the job is waiting on that order. Declines silently for any other state,
 * so callers can invoke it after every assignment without checking first.
 *
 * Not used by the job detail view's no-purchase-order prompt: that flow is already on its way to
 * Awaiting Order, so asking again would be asking twice for one decision.
 *
 * Returns true only when the state actually changed, so a caller can tell a plain refresh from
 * one that needs to reflect a new state.
 */
export async function offerAdvanceToAwaitingOrder(job: {
  id: string;
  state: FabricationJobState;
  recordVersion: string;
}): Promise<boolean> {
  if (job.state !== FabricationJobState.Planned) return false;

  const confirmed = await AskConfirmation(
    LangFabricationJobActions.offerAwaitingOrderQuestion(),
    getConfirmationButtons()[ConfirmationButtonType.yesNo]
  );
  if (!confirmed) return false;

  const result = await getApiFactory().fabricationJob().transitionFabricationJobState({
    fabricationJobId: job.id,
    targetState: FabricationJobState.AwaitingOrder,
    recordVersion: job.recordVersion
  });
  if (!result) return false;

  fireQuickSuccessToast(
    LangFabricationJobActions.transitionedToast(getFabricationJobStateLabel(FabricationJobState.AwaitingOrder))
  );
  return true;
}
